import React, { useEffect, useMemo, useState } from "react";
import FileIcon from "../components/FileIcon";
import "./ShareFolderPage.css";

function getShareIdFromPath() {
  const parts = window.location.pathname.split("/").filter(Boolean);
  const idx = parts.indexOf("share");
  return idx >= 0 ? parts[idx + 1] : null;
}

function ensureSlash(p) {
  if (!p) return "";
  return p.endsWith("/") ? p : `${p}/`;
}

function toRelativePrefix(full, base) {
  if (!full || !base || !full.startsWith(base)) return "";
  return full.slice(base.length);
}

function stripName(full, currentAbs) {
  const base = ensureSlash(currentAbs);
  return full.startsWith(base)
    ? full.slice(base.length).replace(/\/$/, "")
    : full.replace(/\/$/, "");
}

function formatBytes(bytes = 0) {
  if (!Number.isFinite(bytes)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let idx = 0;
  while (value >= 1024 && idx < units.length - 1) {
    value /= 1024;
    idx += 1;
  }
  return `${value.toFixed(value < 10 && idx > 0 ? 2 : 1)} ${units[idx]}`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR");
}

function fileExtension(name = "") {
  return (name.split(".").pop() || "file").toLowerCase();
}

function IconGrid() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 4h7v7H4V4Zm9 0h7v7h-7V4ZM4 13h7v7H4v-7Zm9 0h7v7h-7v-7Z" />
    </svg>
  );
}

function IconList() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 6h3v3H4V6Zm5 0h11v3H9V6Zm-5 9h3v3H4v-3Zm5 0h11v3H9v-3Z" />
    </svg>
  );
}

function IconFolder() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 6.5A2.5 2.5 0 0 1 5.5 4H10l2 2h6.5A2.5 2.5 0 0 1 21 8.5v8A3.5 3.5 0 0 1 17.5 20h-11A3.5 3.5 0 0 1 3 16.5v-10Z" />
    </svg>
  );
}

function IconFile() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 3h7l5 5v11.5A1.5 1.5 0 0 1 17.5 21h-10A1.5 1.5 0 0 1 6 19.5v-15A1.5 1.5 0 0 1 7.5 3H7Zm6 1.5V9h4.5L13 4.5Z" />
    </svg>
  );
}

function IconArrowLeft() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m10.7 5.3-1.4 1.4L14.59 12l-5.3 5.3 1.42 1.4L17.4 12l-6.7-6.7Z" />
    </svg>
  );
}

function IconExternal() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3Zm5 16a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h6v2H5v12h12v-6h2v6Z" />
    </svg>
  );
}

function IconDownload() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M11 3h2v9.17l2.59-2.58L17 11l-5 5-5-5 1.41-1.41L11 12.17V3Zm-6 14h14v4H5v-4Z" />
    </svg>
  );
}

export default function ShareFolderPage() {
  const shareId = useMemo(getShareIdFromPath, []);
  const API_BASE =
    import.meta.env.VITE_BACKEND_URL || "https://gateway.viadrive.com";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bucket, setBucket] = useState("");
  const [basePrefix, setBasePrefix] = useState("");
  const [absPrefix, setAbsPrefix] = useState("");
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("name");
  const [relPrefix, setRelPrefix] = useState(() => {
    const qs = new URLSearchParams(window.location.search);
    return ensureSlash(qs.get("prefix") || "");
  });

  async function load(prefixRel = relPrefix) {
    try {
      setLoading(true);
      setError("");

      const url =
        `${API_BASE}/api/share/${shareId}` +
        (prefixRel ? `?prefix=${encodeURIComponent(prefixRel)}` : "");

      const res = await fetch(url);
      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      setBucket(data.bucket);
      setBasePrefix(data.basePrefix);
      setAbsPrefix(data.prefix);
      setFolders(data.folders || []);
      setFiles(data.files || []);
    } catch (e) {
      setError(e.message || "Erro ao carregar compartilhamento");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(relPrefix);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId, relPrefix]);

  function navigateToRelative(prefix) {
    const rel = ensureSlash(prefix || "");
    const qs = new URLSearchParams(window.location.search);
    if (!rel) qs.delete("prefix");
    else qs.set("prefix", rel);

    window.history.pushState(
      {},
      "",
      `${window.location.pathname}${qs.toString() ? `?${qs.toString()}` : ""}`
    );

    setRelPrefix(rel);
  }

  function parentRelPrefix() {
    if (!relPrefix) return "";
    const parts = relPrefix.split("/").filter(Boolean);
    parts.pop();
    return parts.length ? `${parts.join("/")}/` : "";
  }

  const breadcrumbParts = relPrefix.split("/").filter(Boolean);
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const visibleFolders = useMemo(() => {
    return folders
      .map((folder) => ({
        ...folder,
        _name: stripName(folder.Key, absPrefix),
        _rel: toRelativePrefix(folder.Key, basePrefix),
      }))
      .filter((folder) =>
        normalizedSearch
          ? folder._name.toLowerCase().includes(normalizedSearch)
          : true
      )
      .sort((a, b) => a._name.localeCompare(b._name, "pt-BR"));
  }, [folders, absPrefix, basePrefix, normalizedSearch]);

  const visibleFiles = useMemo(() => {
    const filtered = files
      .map((file) => ({
        ...file,
        _name: stripName(file.Key, absPrefix),
      }))
      .filter((file) =>
        normalizedSearch
          ? file._name.toLowerCase().includes(normalizedSearch)
          : true
      );

    filtered.sort((a, b) => {
      if (sortBy === "date") {
        return new Date(b.lastModified || 0) - new Date(a.lastModified || 0);
      }
      if (sortBy === "size") {
        return (b.size || 0) - (a.size || 0);
      }
      return a._name.localeCompare(b._name, "pt-BR");
    });

    return filtered;
  }, [files, absPrefix, normalizedSearch, sortBy]);

  const totalFileBytes = visibleFiles.reduce(
    (acc, file) => acc + (Number(file.size) || 0),
    0
  );

  if (loading) {
    return (
      <div className="share-folder-page">
        <div className="share-folder-shell share-folder-state">
          <div className="share-folder-spinner" />
          <h2>Carregando compartilhamento</h2>
          <p>Buscando arquivos e pastas publicados.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="share-folder-page">
        <div className="share-folder-shell share-folder-state share-folder-error">
          <span className="share-folder-error-badge">Erro</span>
          <h2>Não foi possível abrir este compartilhamento</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="share-folder-page">
      <div className="share-folder-shell">
        <header className="share-folder-hero">
          <div className="share-folder-brand">
            <img src="/logo_viadrive.svg" alt="ViaDrive" />
            <span className="share-folder-brand-badge">Link público</span>
          </div>

          <div className="share-folder-hero-grid">
            <div className="share-folder-hero-copy">
              <span className="share-folder-eyebrow">Compartilhamento de pasta</span>
              <h1>Arquivos prontos para acesso</h1>
              <p>
                Navegue pelo conteúdo publicado, explore subpastas e abra ou baixe
                arquivos com uma interface mais clara.
              </p>
            </div>

            <div className="share-folder-summary-card">
              <div className="share-folder-summary-top">
                <span className="share-folder-summary-label">Origem</span>
                <strong>{bucket}</strong>
              </div>
              <div className="share-folder-summary-path" title={absPrefix}>
                {absPrefix}
              </div>
              <div className="share-folder-stats">
                <div className="share-folder-stat">
                  <span>Pastas</span>
                  <strong>{visibleFolders.length}</strong>
                </div>
                <div className="share-folder-stat">
                  <span>Arquivos</span>
                  <strong>{visibleFiles.length}</strong>
                </div>
                <div className="share-folder-stat">
                  <span>Tamanho</span>
                  <strong>{formatBytes(totalFileBytes)}</strong>
                </div>
              </div>
            </div>
          </div>
        </header>

        <section className="share-folder-panel">
          <div className="share-folder-breadcrumb-bar">
            <button
              type="button"
              className="share-folder-back"
              onClick={() => navigateToRelative(parentRelPrefix())}
              disabled={!relPrefix}
            >
              <IconArrowLeft />
            </button>

            <div className="share-folder-crumbs">
              <button
                type="button"
                className="share-folder-crumb is-root"
                onClick={() => navigateToRelative("")}
              >
                raiz
              </button>

              {breadcrumbParts.map((part, index) => {
                const partial = `${breadcrumbParts.slice(0, index + 1).join("/")}/`;
                return (
                  <React.Fragment key={partial}>
                    <span className="share-folder-crumb-separator">/</span>
                    <button
                      type="button"
                      className="share-folder-crumb"
                      onClick={() => navigateToRelative(partial)}
                    >
                      {part}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="share-folder-toolbar">
            <div className="share-folder-search">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar arquivos ou pastas"
              />
            </div>

            <div className="share-folder-toolbar-actions">
              <div className="share-folder-view-toggle" role="group" aria-label="Modo de visualização">
                <button
                  type="button"
                  className={viewMode === "grid" ? "is-active" : ""}
                  onClick={() => setViewMode("grid")}
                >
                  <IconGrid />
                  <span>Grid</span>
                </button>
                <button
                  type="button"
                  className={viewMode === "list" ? "is-active" : ""}
                  onClick={() => setViewMode("list")}
                >
                  <IconList />
                  <span>Lista</span>
                </button>
              </div>

              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">Ordenar: nome</option>
                <option value="date">Ordenar: data</option>
                <option value="size">Ordenar: tamanho</option>
              </select>
            </div>
          </div>

          <div className="share-folder-sections">
            <section className="share-folder-section">
              <div className="share-folder-section-heading">
                <div>
                  <span className="share-folder-section-icon"><IconFolder /></span>
                  <div>
                    <h2>Pastas</h2>
                    <p>Navegue pelo conteúdo organizado em subdiretórios.</p>
                  </div>
                </div>
              </div>

              {visibleFolders.length === 0 ? (
                <div className="share-folder-empty">
                  <strong>Nenhuma subpasta neste nível</strong>
                  <span>Se houver arquivos publicados aqui, eles aparecem logo abaixo.</span>
                </div>
              ) : (
                <div
                  className={
                    viewMode === "grid"
                      ? "share-folder-grid"
                      : "share-folder-folder-list"
                  }
                >
                  {visibleFolders.map((folder) => (
                    <button
                      type="button"
                      key={folder.Key}
                      className="share-folder-card"
                      onClick={() => navigateToRelative(folder._rel)}
                    >
                      <div className="share-folder-card-icon">
                        <FileIcon extension="folder" />
                      </div>
                      <div className="share-folder-card-copy">
                        <strong>{folder._name}</strong>
                        <span>Abrir pasta</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="share-folder-section">
              <div className="share-folder-section-heading">
                <div>
                  <span className="share-folder-section-icon"><IconFile /></span>
                  <div>
                    <h2>Arquivos</h2>
                    <p>Abra em nova aba ou baixe diretamente do link público.</p>
                  </div>
                </div>
              </div>

              {visibleFiles.length === 0 ? (
                <div className="share-folder-empty">
                  <strong>Nenhum arquivo encontrado</strong>
                  <span>Ajuste a busca ou volte para outro nível da pasta.</span>
                </div>
              ) : (
                <div className="share-folder-files-shell">
                  <div className="share-folder-files-head">
                    <span>Arquivo</span>
                    <span>Atualizado</span>
                    <span>Tamanho</span>
                    <span className="is-actions">Ações</span>
                  </div>

                  <div className="share-folder-files-body">
                    {visibleFiles.map((file) => (
                      <article key={file.Key} className="share-folder-file-row">
                        <div className="share-folder-file-main">
                          <div className="share-folder-file-icon">
                            <FileIcon extension={fileExtension(file._name)} />
                          </div>
                          <div className="share-folder-file-copy">
                            <strong title={file._name}>{file._name}</strong>
                            <span>{fileExtension(file._name).toUpperCase()}</span>
                          </div>
                        </div>

                        <div className="share-folder-file-meta">
                          {formatDate(file.lastModified)}
                        </div>

                        <div className="share-folder-file-size">
                          {formatBytes(Number(file.size) || 0)}
                        </div>

                        <div className="share-folder-file-actions">
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                            className="share-folder-action action-secondary"
                          >
                            <IconExternal />
                            <span>Abrir</span>
                          </a>
                          <a
                            href={file.url}
                            download
                            className="share-folder-action action-primary"
                          >
                            <IconDownload />
                            <span>Baixar</span>
                          </a>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              )}
            </section>
          </div>
        </section>
      </div>
    </div>
  );
}
